import React, { useState, useEffect, useRef } from 'react';
import { useUserAndGameContext } from '../../../contexts/UserAndGameContext';
import { useQuickPickGamesContext } from '../../QuickPickGames/context/QuickPickGamesContext';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { Box, IconButton, MenuItem, Select } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { DateTime } from 'luxon';
import './MyPicksNavBar.scss';

/**
 * A flexible navigation bar that works with both interval and date-based filtering
 */
const QuickPicksNavBar = ({ selectedTab }) => {
    const {
        intervalInfo,
        myPicksNavBarInfo,
        gameId,
        masterPoolId,
        page,
        gameAddress,
        pickIndex,
        unsavedPicks,
        setUnsavedPicksModalOpen,
        setUrlForNav,
        interval
    } = useUserAndGameContext();
    
    const {
        filterByDate,
        uniqueDays,
        selectedDate,
        dayOptions,
        firstInterval,
        lastInterval,
        dateToIntervalMap,
        intervalToDatesMap,
        defaultDate,
        theCurrentInterval,
    } = useQuickPickGamesContext();
    
    const navigate = useNavigate();
    const location = useLocation();
    const [selectedIndex, setSelectedIndex] = useState(0);
    const isInitialMount = useRef(true);
    
    // Parse URL parameters directly to always prioritize URL state
    const searchParams = new URLSearchParams(location.search);
    const gameDateParam = searchParams.get('gameDate');
    const intervalParam = searchParams.get('interval');
    
    // Determine if we should use dates based on both filterByDate AND selectedTab
    const useDates = filterByDate && selectedTab !== 'interval';
    
    // Determine which options to use based on filtering mode
    const options = useDates ? uniqueDays : 
        Array.from({ length: lastInterval - firstInterval + 1 }, (_, i) => firstInterval + i);
    
    // Get custom interval labels if available
    const getIntervalLabel = (intervalValue) => {
        // If we have custom labels defined in intervalInfo
        if (intervalInfo?.info && Array.isArray(intervalInfo.info)) {
            const intervalObj = intervalInfo.info.find(item => item.interval === intervalValue);
            if (intervalObj && intervalObj.label) {
                return intervalObj.label;
            }
        }
        
        // Default fallback based on type
        if (intervalInfo?.type === 'weeks') {
            return `Week ${intervalValue}`;
        }
        
        // Generic fallback
        return `Interval ${intervalValue}`;
    };
    
    // Format options for display
    const formattedOptions = (
        options?.filter(value => {
          // Only filter when tab is "interval"
          return selectedTab === 'interval'
            ? Number(value) <= theCurrentInterval
            : true;
        }).map(value => ({
          value: value,
          label: useDates ? formatDateForDisplay(value) : getIntervalLabel(Number(value))
        })) || []
      );
      
    
    // Initialize selected index based on URL parameters first, then fall back to context state
    useEffect(() => {
        if (!options?.length) return;
        
        let indexToUse = -1;
        
        // Get the LATEST parameters from the URL (not cached from closure)
        const currentParams = new URLSearchParams(window.location.search);
        const currentIntervalParam = currentParams.get('interval');
        const currentGameDateParam = currentParams.get('gameDate');
    
        // Priority 1: Use URL parameters if available
        if (useDates && currentGameDateParam) {
            indexToUse = options.findIndex(date => date === currentGameDateParam);
        } else if (!useDates && currentIntervalParam) {
            indexToUse = options.findIndex(int => int === Number(currentIntervalParam));
        }
        
        // Priority 2: Fall back to context state
        if (indexToUse === -1) {
            if (useDates && selectedDate) {
                indexToUse = options.findIndex(date => date === selectedDate);
            } else if (!useDates && interval) {
                indexToUse = options.findIndex(int => int === Number(interval));
            }
        }
        
        // Priority 3: Use defaultDate as last resort
        if (indexToUse === -1 && useDates && defaultDate) {
            indexToUse = options.findIndex(date => date === defaultDate);
        }
        
        // Set the index if we found one
        if (indexToUse !== -1) {
            setSelectedIndex(indexToUse);
        } else if (options.length > 0) {
            // As a final fallback, just use the first option
            setSelectedIndex(0);
        }
    }, [
        options, 
        filterByDate, 
        selectedDate, 
        interval, 
        defaultDate,
        selectedTab, // Added this dependency to react to tab changes
        location.search // Most importantly, include location.search to react to URL changes
    ]);

    // Create a ref to track the last tab we handled
    const lastHandledTabRef = useRef(null);

    // Add this useEffect to handle tab changes specifically
    useEffect(() => {
    // Only run this effect if it's not the initial mount
    if (isInitialMount.current) {
        isInitialMount.current = false;
        return;
    }

    // Check if we've already handled this tab change
    if (lastHandledTabRef.current === selectedTab) return;
    
    // Update our ref to remember we've handled this tab
    lastHandledTabRef.current = selectedTab;

    // Build new URL with default values for the new tab
    const newParams = new URLSearchParams(window.location.search);
    newParams.set('tab', selectedTab);

    // Set default values based on the selected tab
    if (selectedTab === 'interval') {
        newParams.set('interval', theCurrentInterval);
        newParams.delete('gameDate'); // Remove gameDate when switching to interval tab
    } else if (selectedTab === 'dates' && defaultDate) {
        const correspondingInterval = dateToIntervalMap[defaultDate] || theCurrentInterval;
        newParams.set('gameDate', defaultDate);
        newParams.set('interval', correspondingInterval);
    } else if (selectedTab === 'overall') {
        // For overall view, we might want to remove both parameters
        newParams.delete('interval');
        newParams.delete('gameDate');
    }

    // Preserve other important URL parameters
    if (masterPoolId) newParams.set('mId', masterPoolId);
    if (gameId) newParams.set('gId', gameId);
    if (page) newParams.set('page', page);
    if (pickIndex) newParams.set('pickIndex', pickIndex);

    // Navigate to the new URL
    const url = `/${encodeURIComponent(gameAddress)}?${newParams.toString()}`;
    navigate(url, { replace: true });
    }, [selectedTab, theCurrentInterval, defaultDate, dateToIntervalMap, gameAddress, masterPoolId, gameId, page, pickIndex, navigate]);

    function navigateToNewSelection(value) {
        // Build URL with all current params
        const searchParams = new URLSearchParams(window.location.search);
        
        if (useDates) {
            // If date-based filtering, set both interval and gameDate
            const correspondingInterval = dateToIntervalMap[value] || interval;
            searchParams.set('interval', correspondingInterval);
            searchParams.set('gameDate', value);
        } else {
            // If interval-based filtering, just set interval
            searchParams.set('interval', value);
            // If we're in interval mode but still have filterByDate=true,
            // we need to handle the gameDate param differently
            if (filterByDate) {
                // Use the intervalToDatesMap to find the first date for this interval if available
                const datesForInterval = intervalToDatesMap && intervalToDatesMap[value];
                if (datesForInterval && datesForInterval.length > 0) {
                    searchParams.set('gameDate', datesForInterval[0]);
                } else {
                    // If no mapping available, remove gameDate param
                    searchParams.delete('gameDate');
                }
            } else {
                // Standard interval mode - remove any gameDate param
                searchParams.delete('gameDate');
            }
        }
        
        // Add other standard params
        if (masterPoolId) searchParams.set('mId', masterPoolId);
        if (gameId) searchParams.set('gId', gameId);
        if (page) searchParams.set('page', page);
        if (pickIndex) searchParams.set('pickIndex', pickIndex);
        
        // Create the full URL
        const encodedAddress = encodeURIComponent(gameAddress);
        const url = `/${encodedAddress}?${searchParams.toString()}`;
        
        // Use navigate with replace option to ensure React Router updates correctly
        navigate(url, { replace: true });
    }
    
    // Helper function to format date for display
    function formatDateForDisplay(dateString) {
        if (!dateString) return '';
        try {
            const date = DateTime.fromISO(dateString);
            if (!date.isValid) {
                console.error("Invalid date:", dateString);
                return String(dateString); // Return the string representation as fallback
            }
            return date.toFormat('EEE MMM d'); // Format as "Wed Apr 16"
        } catch (error) {
            console.error("Error formatting date:", error, dateString);
            return String(dateString);
        }
    }
    
    // Handle arrow navigation
    function handleArrowClick(direction) {
        if (!options?.length) return;
        
        let newIndex;
        if (direction === 'next') {
            newIndex = Math.min(selectedIndex + 1, options.length - 1);
        } else {
            newIndex = Math.max(selectedIndex - 1, 0);
        }
        
        const newValue = options[newIndex];
        
        if (unsavedPicks) {
            // Handle unsaved picks case
            setUnsavedPicksModalOpen(true);
            
            // Create URL for navigation after save
            const searchParams = new URLSearchParams();
            if (useDates) {
                const newInterval = dateToIntervalMap[newValue] || interval;
                searchParams.set('interval', newInterval);
                searchParams.set('gameDate', newValue);
            } else {
                searchParams.set('interval', newValue);
                // Handle gameDate parameter for interval mode
                if (filterByDate) {
                    const datesForInterval = intervalToDatesMap && intervalToDatesMap[newValue];
                    if (datesForInterval && datesForInterval.length > 0) {
                        searchParams.set('gameDate', datesForInterval[0]);
                    }
                }
            }
            searchParams.set('mId', masterPoolId);
            searchParams.set('gId', gameId);
            searchParams.set('page', page);
            searchParams.set('pickIndex', pickIndex);
            
            setUrlForNav(`/${encodeURIComponent(gameAddress)}?${searchParams.toString()}`);
            return;
        }
        
        // Update the index and navigate
        setSelectedIndex(newIndex);
        navigateToNewSelection(newValue);
    }
    
    // Handle dropdown selection
    function handleSelectChange(event) {
        const newValue = event.target.value;
        const newIndex = options.findIndex(opt => 
            useDates ? opt === newValue : Number(opt) === Number(newValue)
        );
        
        if (unsavedPicks) {
            // Handle unsaved picks case
            setUnsavedPicksModalOpen(true);
            
            // Create URL for navigation after save
            const searchParams = new URLSearchParams();
            if (useDates) {
                const newInterval = dateToIntervalMap[newValue] || interval;
                searchParams.set('interval', newInterval);
                searchParams.set('gameDate', newValue);
            } else {
                searchParams.set('interval', newValue);
                // Handle gameDate parameter for interval mode
                if (filterByDate) {
                    const datesForInterval = intervalToDatesMap && intervalToDatesMap[newValue];
                    if (datesForInterval && datesForInterval.length > 0) {
                        searchParams.set('gameDate', datesForInterval[0]);
                    }
                }
            }
            searchParams.set('mId', masterPoolId);
            searchParams.set('gId', gameId);
            searchParams.set('page', page);
            searchParams.set('pickIndex', pickIndex);
            
            setUrlForNav(`/${encodeURIComponent(gameAddress)}?${searchParams.toString()}`);
            return;
        }
        
        // Update the index and navigate
        if (newIndex >= 0) {
            setSelectedIndex(newIndex);
        }
        navigateToNewSelection(newValue);
    }
    
    // Don't render if no options
    if (!options?.length) return null;
    
    // Get current value for display
    const currentValue = options[selectedIndex] || '';
    
    // Match the display value logic with the options logic - use useDates variable
    const displayValue = useDates
        ? formatDateForDisplay(currentValue) 
        : getIntervalLabel(Number(currentValue));
    
    return (
        <Box 
            className="my-picks-nav-bar"
            sx={{ 
                backgroundColor: myPicksNavBarInfo?.backgroundColor || '#07356B',
                color: myPicksNavBarInfo?.textColor || '#FFC60A',
            }}
        >
            <Box className="my-picks-nav-bar-container">
                <Box className="my-picks-nav-bar-content">
                    <IconButton 
                        onClick={() => handleArrowClick('prev')}
                        disabled={selectedIndex === 0}
                    >
                        <ArrowBackIosNewIcon
                            className="my-picks-nav-bar-arrow-icon" 
                            sx={{ color: myPicksNavBarInfo?.textColor || '#FFC60A' }}
                        />
                    </IconButton>
                    <Box className="my-picks-nav-bar-select-container">
                        <Select
                            value={currentValue}
                            onChange={handleSelectChange}
                            variant="outlined"
                            className="my-picks-nav-bar-select"
                            sx={{ 
                                color: myPicksNavBarInfo?.textColor || '#FFC60A',
                                '& .MuiSvgIcon-root': {
                                    color: myPicksNavBarInfo?.textColor || '#FFC60A'
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'transparent'
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'transparent'
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'transparent'
                                }
                            }}
                            renderValue={() => displayValue}
                        >
                            {formattedOptions?.map((option) => (
                                <MenuItem 
                                    key={option.value} 
                                    value={option.value}
                                    sx={{ color: '#000000' }}
                                >
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>
                    <IconButton 
                        onClick={() => handleArrowClick('next')}
                        disabled={selectedIndex >= formattedOptions?.length - 1}
                    >
                        <ArrowBackIosNewIcon 
                            className="my-picks-nav-bar-arrow-icon"
                            sx={{ color: myPicksNavBarInfo?.textColor || '#FFC60A', transform: 'rotate(180deg)' }}
                        />
                    </IconButton>
                </Box>
            </Box>
        </Box>
    );
};

export default QuickPicksNavBar;